import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, Newspaper, ExternalLink } from 'lucide-react'
import { newsItems } from './newsData'

const ITEMS_PER_PAGE = 6

export function NewsGrid() {
  const { t, i18n } = useTranslation('common')
  const [currentPage, setCurrentPage] = useState(1)
  const currentLang = i18n.language
  const newsPath = `/${currentLang}/news`

  const totalPages = Math.ceil(newsItems.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const visibleItems = newsItems.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const locale = i18n.language === 'it' ? 'it-IT' : 'en-US'
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of news section
    const newsSection = document.getElementById('news-grid')
    if (newsSection) {
      newsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const featuredNews = newsItems.filter(item => item.featured)
  const regularNews = visibleItems.filter(item => !item.featured)

  return (
    <section id="news-grid" className="section-padding bg-[#FDF8F3]" aria-labelledby="news-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold rounded-full mb-4">
            {t('news.grid.badge')}
          </span>
          <h2 id="news-title" className="heading-section text-[#4E2118] mb-4">
            {t('news.grid.title')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70 max-w-2xl mx-auto">
            {t('news.grid.subtitle')}
          </p>
        </motion.div>

        {/* Featured News - Large Cards */}
        {currentPage === 1 && featuredNews.length > 0 && (
          <div className="mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredNews.map((news, index) => (
                <motion.article
                  key={news.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500"
                >
                  <Link to={`${newsPath}/${news.id}`} className="block">
                    {/* Image Container */}
                    <div className="relative h-64 lg:h-80 overflow-hidden">
                      <img
                        src={news.image}
                        alt={t(news.titleKey)}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback if image fails to load
                          const target = e.target as HTMLImageElement
                          target.src = '/images/news_iniziative.png'
                        }}
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/80 via-[#4E2118]/20 to-transparent" />

                      {/* Category Badge */}
                      <div className="absolute top-6 left-6 px-4 py-2 bg-[#DF631E] text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-2">
                        <Newspaper className="w-4 h-4" />
                        {news.category}
                      </div>

                      {/* Featured Badge */}
                      <div className="absolute top-6 right-6 px-3 py-1.5 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full shadow-lg">
                        {t('news.grid.featured')}
                      </div>

                      {/* Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                        {/* Date */}
                        <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                          <Calendar className="w-4 h-4" />
                          <time dateTime={news.date}>{formatDate(news.date)}</time>
                        </div>

                        {/* Title */}
                        <h3 className="font-serif text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-[#F3E4D6] transition-colors leading-tight">
                          {t(news.titleKey)}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-white/80 text-base leading-relaxed line-clamp-2 mb-4">
                          {t(news.excerptKey)}
                        </p>

                        {/* Read More Link */}
                        <span className="inline-flex items-center gap-2 text-white font-semibold group-hover:text-[#DF631E] transition-colors">
                          {t('buttons.readMore')}
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        )}

        {/* Regular News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regularNews.map((news, index) => (
            <motion.article
              key={news.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 flex flex-col"
            >
              <Link to={`${newsPath}/${news.id}`} className="block flex flex-col h-full">
                {/* Image Container */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={news.image}
                    alt={t(news.titleKey)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback if image fails to load
                      const target = e.target as HTMLImageElement
                      target.src = '/images/news_iniziative.png'
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 via-transparent to-transparent" />

                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full shadow-sm">
                    {news.category}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Date */}
                  <div className="flex items-center gap-2 text-[#4E2118]/60 text-sm mb-3">
                    <Calendar className="w-4 h-4 text-[#DF631E]" />
                    <time dateTime={news.date}>{formatDate(news.date)}</time>
                  </div>

                  {/* Title */}
                  <h3 className="font-serif text-xl font-bold text-[#4E2118] mb-3 group-hover:text-[#DF631E] transition-colors leading-tight line-clamp-2">
                    {t(news.titleKey)}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-[#4E2118]/70 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
                    {t(news.excerptKey)}
                  </p>

                  {/* Read More Link */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#F3E4D6]">
                    <span className="inline-flex items-center gap-2 text-[#DF631E] font-semibold text-sm group-hover:gap-3 transition-all">
                      {t('buttons.readMore')}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                    {news.external && (
                      <ExternalLink className="w-4 h-4 text-[#4E2118]/40" />
                    )}
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-2 mt-12"
          >
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#4E2118]/5 text-[#4E2118]"
              aria-label={t('buttons.previous')}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t('buttons.previous')}</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-full font-medium transition-all ${
                    currentPage === page
                      ? 'bg-[#DF631E] text-white shadow-lg'
                      : 'text-[#4E2118] hover:bg-[#4E2118]/5'
                  }`}
                  aria-label={t('news.pagination.page', { page })}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#4E2118]/5 text-[#4E2118]"
              aria-label={t('buttons.next')}
            >
              <span className="hidden sm:inline">{t('buttons.next')}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Showing X of Y */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center text-sm text-[#4E2118]/60 mt-4"
        >
          {t('news.pagination.showing', {
            start: startIndex + 1,
            end: Math.min(startIndex + ITEMS_PER_PAGE, newsItems.length),
            total: newsItems.length,
          })}
        </motion.p>
      </div>
    </section>
  )
}
